export const INVOICE_STATE_DESCRIPTION_BY_ID = {
  1: "Invoiced",
  2: "Not claimable",
  3: "Ready for claim",
  4: "Removed",
  9: "Rejected",
  10: "Deleted",
};

const INVOICE_STATUS_ALIASES = {
  "Ready For Claim": "Ready for claim",
  "Not Claimable": "Not claimable",
  "To Not Claim": "To not claim",
  "Removed from claim": "Removed",
};

export const normalizeInvoiceStatusKey = (description) => {
  if (!description) {
    return "";
  }

  return INVOICE_STATUS_ALIASES[description] ?? description;
};

export const translateApiDescription = (description, t) => {
  if (!description) {
    return "";
  }

  return t(description);
};

export const resolveInvoiceStatusKey = (invoice, claimOfInvoice, hasPreFinance) => {
  if (hasPreFinance || (invoice.preFinanceId ?? 0) > 0 || (invoice.preFinanced ?? 0) > 0) {
    return "Pre-Financed";
  }

  const invoiceStateId = invoice.invoiceStateId;

  if (invoiceStateId === 3) {
    if (claimOfInvoice?.claimStateId === 4) {
      return "Pending";
    }

    if (claimOfInvoice?.claimStateId === 16) {
      return "Reimbursed";
    }

    return INVOICE_STATE_DESCRIPTION_BY_ID[3];
  }

  const backendStatus = normalizeInvoiceStatusKey(
    invoice.invoiceStatusDescription ?? invoice.InvoiceStatusDescription ?? invoice.invoiceStatusDesc ?? ""
  );

  if (backendStatus) {
    return backendStatus;
  }

  return INVOICE_STATE_DESCRIPTION_BY_ID[invoiceStateId] ?? "";
};

export const translateInvoiceStatusKey = (statusKey, t) => {
  if (!statusKey) {
    return "";
  }

  return t(statusKey);
};

export const resolveInvoiceStatusDescription = (
  { invoiceStateId, invoiceStateDescription, preFinanceId, claimStateId },
  t
) => {
  const statusKey = resolveInvoiceStatusKey(
    {
      invoiceStateId,
      preFinanceId,
      invoiceStatusDescription: invoiceStateDescription,
    },
    claimStateId ? { claimStateId } : null,
    (preFinanceId ?? 0) > 0
  );

  return translateInvoiceStatusKey(statusKey, t);
};

export const getInvoiceStatusDisplayValue = (viewCustomerInvoiceDialog, invoiceStateData, invoiceStateId, t) => {
  const params = viewCustomerInvoiceDialog?.params;
  const claimStateId = viewCustomerInvoiceDialog?.claimStateId;
  const claimOfInvoice = claimStateId ? { claimStateId } : null;

  const statusKey = resolveInvoiceStatusKey(
    {
      ...params,
      invoiceStateId,
      invoiceStatusDesc: params?.invoiceStatusDesc ?? params?.invoiceStatusDescription ?? params?.InvoiceStatusDescription,
    },
    claimOfInvoice,
    (params?.preFinanceId ?? 0) > 0
  );

  if (statusKey) {
    return translateInvoiceStatusKey(statusKey, t);
  }

  const stateRecord = invoiceStateData.find((state) => state.invoiceStateId === invoiceStateId);

  return resolveInvoiceStatusDescription(
    {
      invoiceStateId,
      invoiceStateDescription: stateRecord?.invoiceStateDescription,
      preFinanceId: params?.preFinanceId ?? 0,
      claimStateId,
    },
    t
  );
};

export const getInvoiceListStatusDisplayValue = (row, claimById, t) => {
  const claimOfInvoice = claimById?.get?.(row.claimId);
  const statusKey = row.invoiceStatusDesc || resolveInvoiceStatusKey(row, claimOfInvoice, (row.preFinanceId ?? 0) > 0);

  return translateInvoiceStatusKey(statusKey, t);
};

export const getInvoiceListLabelDisplayValue = (row, t, fallbackDescription) => {
  return translateInvoiceLabel(
    {
      shortCode: row?.invoiceLabelShortCode,
      description: fallbackDescription ?? row?.invoiceLabelDesc ?? row?.invoiceLabelDescription,
    },
    t
  );
};

const INVOICE_LABEL_KEYS_BY_SHORT_CODE = {
  CPY: "Copy Invoice",
  CVL: "Claimable VAT amount too low",
  NCP: "Not claimable product",
  NOV: "No VAT on the invoice",
  EXP: "Claim period expired",
  IAC: "Invoice already claimed",
  RAI: "Rejected after information request",
  RCA: "Reverse charge applicable",
  NVI: "Non-compliant VAT Invoice",
  NEU: "Non-EU country",
  NEC: "Entity not eligible to claim VAT",
  CSU: "Claim slot already used by third party",
  ENF: "Economically not feasible",
};

const INVOICE_LABEL_DESCRIPTION_ALIASES = {
  "Copy Invoice": "Copy Invoice",
  "Claimable VAT amount too low": "Claimable VAT amount too low",
  "Not claimable product": "Not claimable product",
  "No VAT on the invoice": "No VAT on the invoice",
  "Claim period expired": "Claim period expired",
  "Invoice already claimed": "Invoice already claimed",
  "Rejected after information request": "Rejected after information request",
  "Reverse charge applicable": "Reverse charge applicable",
  "Non-compliant VAT Invoice": "Non-compliant VAT Invoice",
  "Non-EU country": "Non-EU country",
  "Entity not eligible to claim VAT": "Entity not eligible to claim VAT",
  "Claim slot already used by third party": "Claim slot already used by third party",
  "Economically not feasible": "Economically not feasible",
};

export const resolveInvoiceLabelKey = ({ shortCode, description } = {}) => {
  const normalizedShortCode = shortCode?.trim()?.toUpperCase();
  if (normalizedShortCode && INVOICE_LABEL_KEYS_BY_SHORT_CODE[normalizedShortCode]) {
    return INVOICE_LABEL_KEYS_BY_SHORT_CODE[normalizedShortCode];
  }

  const normalizedDescription = description?.trim();
  if (!normalizedDescription) {
    return "";
  }

  return INVOICE_LABEL_DESCRIPTION_ALIASES[normalizedDescription] ?? normalizedDescription;
};

export const translateInvoiceLabel = ({ shortCode, description }, t) => {
  const labelKey = resolveInvoiceLabelKey({ shortCode, description });
  if (!labelKey) {
    return "";
  }

  return t(labelKey);
};
